import type { ReactNode } from 'react';
import { Icon } from './icons';
import type { PagedList } from '../hooks/usePagedList';

export interface Column {
  label: string;
  align?: 'left' | 'center' | 'right';
  /** Narrow columns that must not be squeezed by a long neighbour. */
  width?: string;
}

interface Props<T> {
  list: PagedList<T>;
  columns: (string | Column)[];
  /** The `<tr>` rows for this page. */
  children: ReactNode;
  searchPlaceholder: string;
  /** The add button. Omit it for a read-only table, or when the user may not write. */
  action?: { label: string; onClick: () => void };
  /** Shown when the table is genuinely empty. May carry an action, like clearing a filter. */
  empty: ReactNode;
  /** Shown instead of `empty` while a search term is active. */
  emptyFiltered?: ReactNode;
  /** Extra controls rendered at the start of the toolbar, such as select filters. */
  filters?: ReactNode;
}

const asColumn = (c: string | Column): Column => (typeof c === 'string' ? { label: c } : c);

/**
 * The shell every master-data table shares: a search box, an add button, the table
 * itself, and a server-side pager.
 *
 * Only the shell is shared. Each page still writes its own rows, because the columns are
 * where these tables genuinely differ and a config-driven cell renderer would be harder
 * to read than the markup it replaced.
 */
export function DataTable<T>({
  list, columns, children, searchPlaceholder, action, empty, emptyFiltered, filters,
}: Props<T>) {
  const cols = columns.map(asColumn);
  const message = list.loading && list.items.length === 0 ? 'Memuat…'
    : list.items.length === 0 ? (list.filtered ? (emptyFiltered ?? 'Tidak ada baris yang cocok dengan pencarian.') : empty)
    : null;

  return (
    <div className="table-block">
      <div className="table-toolbar">
        {filters}
        <label className="search">
          <Icon name="search" size={16} />
          <input
            type="search"
            value={list.term}
            onChange={(e) => list.setTerm(e.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
          />
        </label>
        {action && (
          <button type="button" className="primary table-action" onClick={action.onClick}>
            <Icon name="plus" size={16} />
            {action.label}
          </button>
        )}
      </div>

      {list.error && <div className="msg error">{list.error}</div>}

      <div className="card table-card">
        <div className="table-scroll">
          <table aria-busy={list.loading}>
            <thead>
              <tr>
                {cols.map((c) => (
                  <th key={c.label} style={{ textAlign: c.align ?? 'left', width: c.width }}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className={list.loading ? 'is-loading' : undefined}>
              {message
                ? <tr><td colSpan={cols.length} className="muted table-empty">{message}</td></tr>
                : children}
            </tbody>
          </table>
        </div>

        <Pager list={list} />
      </div>
    </div>
  );
}

/**
 * Page navigation. Prev/next only — a numbered pager would need the page count in the
 * toolbar to stay useful, and the row range already tells the reader where they are.
 */
function Pager<T>({ list }: { list: PagedList<T> }) {
  const { page, totalPages, total, range } = list;

  return (
    <nav className="pager" aria-label="Navigasi halaman">
      <p className="pager-count">
        {total === 0
          ? 'Tidak ada baris'
          : <>Baris <strong>{range[0]}–{range[1]}</strong> dari <strong>{total}</strong></>}
      </p>
      <div className="pager-controls">
        <button
          type="button"
          className="icon-btn"
          onClick={() => list.setPage(page - 1)}
          disabled={page <= 1 || list.loading}
          aria-label="Halaman sebelumnya"
        >
          <Icon name="prev" size={18} />
        </button>
        <span className="pager-page">Hal. {page} / {totalPages}</span>
        <button
          type="button"
          className="icon-btn"
          onClick={() => list.setPage(page + 1)}
          disabled={page >= totalPages || list.loading}
          aria-label="Halaman berikutnya"
        >
          <Icon name="next" size={18} />
        </button>
      </div>
    </nav>
  );
}
